import { roleAtLeast, type UserRole } from '@inou/shared';

import { NavAction } from '@/components/nav/EntityLink';
import { ONBOARDING_STEPS } from '@/components/onboarding/steps';
import type { OnboardingStatus } from '@/lib/api';

/**
 * 開通直後のチェックリスト。
 *
 * 済み / 未済は API が実データから導出したものをそのまま出す (画面では数えない)。
 * 出すのは**自分が実行できるステップだけ**で、飛び先は `NavAction` に通すので
 * 開けない画面へのリンクは出ない。
 */
export function OnboardingChecklist({
  status,
  role,
}: {
  status: OnboardingStatus;
  role: UserRole;
}) {
  const done = new Set(status.steps.filter((step) => step.done).map((step) => step.id));
  const steps = ONBOARDING_STEPS.filter(
    (step) => roleAtLeast(role, step.minRole) && status.steps.some((entry) => entry.id === step.id),
  );

  return (
    <ol className="flex flex-col border border-line bg-surface-raised">
      {steps.map((step, index) => {
        const finished = done.has(step.id);
        return (
          /*
           * 768px 未満では状態とボタンを下の段に落とす。同じ行に並べたままだと、
           * `w-32` のボタンが幅を持っていって手順の見出しが 1 文字ずつ折り返す。
           * 番号と本文は 1 つの箱にまとめてあるが、768px 以上の間隔と幅は
           * まとめる前と同じ (li の gap-4 が箱とボタンの間、箱の gap-4 が
           * 番号と本文の間に入る)。
           */
          <li
            key={step.id}
            className="flex flex-col gap-3 border-line border-b px-4 py-5 last:border-b-0 md:flex-row md:items-start md:gap-4 md:px-6"
          >
            <div className="flex min-w-0 flex-1 items-start gap-4">
              <span className="w-6 shrink-0 pt-0.5 font-mono text-ink-faint text-sm">
                {index + 1}
              </span>

              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span
                  className={finished ? 'text-ink-muted text-sm line-through' : 'font-bold text-sm'}
                >
                  {step.title}
                </span>
                <span className="text-[13px] text-ink-muted leading-relaxed">{step.reason}</span>
              </div>
            </div>

            {/* 下の段に落ちたときは本文の左端に揃える (番号 24px + gap 16px) */}
            <div className="flex shrink-0 items-center gap-3 pl-10 md:pl-0">
              <span
                className={
                  finished
                    ? 'font-mono text-[11px] text-brand-700 tracking-wider'
                    : 'font-mono text-[11px] text-ink-faint tracking-wider'
                }
              >
                {finished ? 'DONE' : 'TODO'}
              </span>
              {/* 幅を揃えて、状態の列が行ごとにずれないようにする */}
              {finished ? null : (
                <NavAction target={step.target} className="w-32">
                  {step.action}
                </NavAction>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
